import type { ChatMessage, ChatResponse } from "@/types/api";
import { platformAuthHeaders } from "@/lib/platformAuth";

export type ChatStreamEvent =
  | { kind: "token"; value: string }
  | { kind: "tool_status"; label: string; toolName: string }
  | { kind: "tool_complete"; toolName: string; summary: string; success: boolean }
  | { kind: "answer"; result: ChatResponse }
  | { kind: "done" }
  | { kind: "error"; message: string };

interface OpenStreamArgs {
  message: string;
  history: ChatMessage[];
  topK?: number;
  signal?: AbortSignal;
  teamsToken?: string | null;
}

function streamAuthHeaders(teamsToken?: string | null): Record<string, string> {
  if (teamsToken) return { Authorization: `Bearer ${teamsToken}` };
  return platformAuthHeaders();
}

/**
 * Async iterator over `/api/chat/stream`. Parses Foundry-style SSE chunks
 * (`data: {json}\n\n` ... `data: [DONE]\n\n`).
 *
 * Important: the underlying endpoint is currently model-only and does not
 * emit MCP-derived sources mid-stream. Sources for streamed answers are
 * therefore not available; the non-streaming `/api/chat` path remains the
 * source of truth for GitHub-grounded responses.
 */
export async function* openChatStream({
  message,
  history,
  topK = 6,
  signal,
  teamsToken,
}: OpenStreamArgs): AsyncGenerator<ChatStreamEvent> {
  const extraHeaders = streamAuthHeaders(teamsToken);
  const response = await fetch("/api/chat/stream", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...extraHeaders },
    body: JSON.stringify({ message, history, topK }),
    signal,
  });

  if (!response.ok || !response.body) {
    let detail = `HTTP ${response.status}`;
    try {
      const text = await response.text();
      if (text) detail = text.slice(0, 300);
    } catch {
      /* ignore */
    }
    yield { kind: "error", message: detail };
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let separatorIndex;
      while ((separatorIndex = buffer.indexOf("\n\n")) >= 0) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        const event = parseSseEvent(rawEvent);
        if (!event) continue;
        if (event.kind === "done") {
          yield event;
          return;
        }
        yield event;
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }

  yield { kind: "done" };
}

/**
 * Async iterator over `/api/chat/status-stream`.
 * Emits tool_status events while the chatbot works, then an answer event
 * with the complete response (reply, sources, followUpQuestions).
 *
 * This replaces the blocking `/api/chat` call for the non-streaming path,
 * giving users live feedback while the AI runs tool calls.
 */
export async function* openChatWithToolStatus({
  message,
  history,
  topK = 6,
  signal,
  teamsToken,
}: OpenStreamArgs): AsyncGenerator<ChatStreamEvent> {
  const extraHeaders = streamAuthHeaders(teamsToken);
  const response = await fetch("/api/chat/status-stream", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...extraHeaders },
    body: JSON.stringify({ message, history, topK }),
    signal,
  });

  if (!response.ok || !response.body) {
    let detail = `HTTP ${response.status}`;
    try {
      const text = await response.text();
      if (text) detail = text.slice(0, 300);
    } catch {
      /* ignore */
    }
    yield { kind: "error", message: detail };
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let separatorIndex;
      while ((separatorIndex = buffer.indexOf("\n\n")) >= 0) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        const event = parseStatusSseEvent(rawEvent);
        if (!event) continue;
        if (event.kind === "done") {
          yield event;
          return;
        }
        yield event;
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }

  yield { kind: "done" };
}

function parseStatusSseEvent(raw: string): ChatStreamEvent | null {
  const lines = raw.split("\n");
  const dataLines = lines
    .map((line) => line.trim())
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim());
  if (dataLines.length === 0) return null;

  const payload = dataLines.join("\n");
  if (!payload) return null;
  if (payload === "[DONE]") return { kind: "done" };

  try {
    const json = JSON.parse(payload) as {
      type?: string;
      value?: string;
      label?: string;
      toolName?: string;
      summary?: string;
      success?: boolean;
      message?: string;
      reply?: string;
      sources?: ChatResponse["sources"];
      followUpQuestions?: string[];
      requestId?: string;
    };

    if (json.type === "token" && typeof json.value === "string") {
      return { kind: "token", value: json.value };
    }
    if (json.type === "tool_status" && json.label) {
      return { kind: "tool_status", label: json.label, toolName: json.toolName ?? "" };
    }
    if (json.type === "tool_complete") {
      return {
        kind: "tool_complete",
        toolName: json.toolName ?? "",
        summary: json.summary ?? "",
        success: json.success !== false,
      };
    }
    if (json.type === "answer") {
      const { type: _type, ...rest } = json as Record<string, unknown>;
      return { kind: "answer", result: rest as unknown as ChatResponse };
    }
    if (json.type === "error") {
      return { kind: "error", message: json.message || "Unknown error" };
    }
  } catch {
    /* ignore unparseable lines */
  }

  return null;
}

function parseSseEvent(raw: string): ChatStreamEvent | null {
  const lines = raw.split("\n");
  const dataLines = lines
    .map((line) => line.trim())
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim());
  if (dataLines.length === 0) return null;

  const payload = dataLines.join("\n");
  if (!payload) return null;
  if (payload === "[DONE]") return { kind: "done" };
  if (payload.startsWith("[ERROR]")) {
    return { kind: "error", message: payload.slice(7).trim() || "stream error" };
  }

  try {
    const json = JSON.parse(payload) as {
      choices?: Array<{ delta?: { content?: string } }>;
      error?: string | { message?: string };
    };
    if (json.error) {
      const message =
        typeof json.error === "string" ? json.error : json.error.message || "stream error";
      return { kind: "error", message };
    }
    const token = json.choices?.[0]?.delta?.content;
    if (typeof token === "string" && token.length > 0) {
      return { kind: "token", value: token };
    }
  } catch {
    /* non-JSON chunk: treat as text */
    return { kind: "token", value: payload };
  }

  return null;
}
